import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LandingFooter from './LandingFooter';
import SpaceBackground from './3D/SpaceBackground';
import Contact from '../Portfolio/Contact';
import SEO from '../SEO';
import '../../styles/landing.css';
import { supabase } from '../../supabase';

const LandingPage = () => {
  const [latestUpdates, setLatestUpdates] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.l-fade-in').forEach(el => observer.observe(el));

    // Fetch latest courses and products
    const fetchLatestData = async () => {
      try {
        const { data: coursesData } = await supabase
          .from('courses')
          .select('*')
          .order('id', { ascending: false })
          .limit(2);

        const { data: productsData } = await supabase
          .from('products')
          .select('*')
          .order('id', { ascending: false })
          .limit(1);

        let updates = [];

        if (coursesData) {
          updates = [
            ...updates,
            ...coursesData.map(course => ({
              id: `course-${course.id}`,
              tag: 'Course',
              title: course.title,
              date: 'New Release',
              img: course.thumbnail_url || '/art/Namal_ict.png',
              link: `/course/${course.id}`
            }))
          ];
        }

        if (productsData) {
          updates = [
            ...updates,
            ...productsData.map(prod => {
              const isSoftware = ['Desktop Apps', 'Web Apps', 'Templates', 'Scripts'].includes(prod.category);
              const isBook = ['Books', 'Tutes', 'Past Papers', 'Model Papers', 'Monthly Tests'].includes(prod.category);
              const storeLink = isBook ? '/store/books' : (isSoftware ? '/store/software' : '/store/electronics');
              
              return {
                id: `prod-${prod.id}`,
                tag: 'Product',
                title: prod.name,
                date: 'New Arrival',
                img: (prod.images && prod.images.length > 0) ? prod.images[0] : 'https://images.unsplash.com/photo-1555664424-778a184335ec?q=80&w=1000&auto=format&fit=crop',
                link: storeLink
              };
            })
          ];
        }

        setLatestUpdates(updates);
      } catch (err) {
        console.error("Error fetching latest updates:", err);
      }
    };

    fetchLatestData();
  }, []);

  return (
    <div className="landing-page">
      <SEO
        title="Namal Chamodya | ICT Educator, Developer, Electronic Engineer & Creative Artist"
        description="Official hub of Namal Chamodya (NC.) — Sri Lanka's creative tech professional offering O/L & A/L ICT classes in Sinhala, full-stack web development, IoT & embedded systems engineering, PCB design, 3D modeling, digital art, and premium educational courses. Explore portfolio, store, blog & learning hub."
        keywords="Namal Chamodya, Namal, Chamodya, NC., nc, Namal ICT, ICT Sinhala, O/L ICT, A/L ICT, advanced level ICT class, ICT teacher Sri Lanka, web developer Sri Lanka, electronic engineer, IoT engineer, embedded systems engineer, PCB designer, 3D designer, digital artist, creative art, writer, editor, Arduino Sri Lanka, full stack developer, Namal Learning Hub, tech educator Sri Lanka"
        canonical="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Namal Chamodya — Official Hub",
          "description": "The official website of Namal Chamodya featuring ICT education, developer portfolio, electronics store, blog, and creative art gallery.",
          "url": "https://namal-chamodya.space/",
          "mainEntity": {
            "@type": "Person",
            "name": "Namal Chamodya"
          }
        }}
      />
      <SpaceBackground />

      {/* --- Hero Section --- */}
      <header className="l-hero l-fade-in">
        <div className="l-hero-content">
          <span className="l-badge">Welcome to the Official Hub</span>
          <h1 className="l-title">
            Discover <span className="l-highlight">Innovation</span> <br />
            & Creativity
          </h1>
          <p className="l-desc">
            Explore my personal ecosystem of Engineering, Education, and Digital Art.
            Whether you are here to learn, buy, or be inspired—you've found the right place.
          </p>
          <div className="l-hero-btns">
            <Link to="/portfolio" className="l-btn primary">View Portfolio</Link>
            <Link to="/store/electronics" className="l-btn secondary">Visit Store</Link>
          </div>
        </div>
      </header>

      {/* --- 3D Showcase --- */}
      <section className="l-section showcase-section l-fade-in" aria-label="Featured 3D Creation">
        <div className="showcase-container">
          <div className="showcase-text">
            <h2 className="l-section-title">Featured <span className="l-highlight">3D Creation</span></h2>
            <div style={{
              borderLeft: '4px solid #f39c12',
              paddingLeft: '15px',
              margin: '15px 0',
              backgroundColor: 'rgba(243, 156, 18, 0.1)',
              padding: '10px'
            }}>
              <p style={{ margin: 0, fontStyle: 'italic', color: '#ddd' }}>
                <strong>⚠️ CRITICAL ADVICE:</strong> <br />
                "Deep space environment detected. To navigate this universe safely,
                equipping this Space Suit is highly recommended."
              </p>
            </div>
            <Link to="/3d-projects" className="l-btn secondary" style={{ marginTop: '20px', display: 'inline-block' }}>
              View in 3D Lab →
            </Link>
          </div>
          <div className="showcase-model">
            <model-viewer
              src="https://modelviewer.dev/shared-assets/models/Astronaut.glb"
              alt="3D Astronaut Model by Namal Chamodya"
              auto-rotate
              camera-controls
              interaction-prompt="none"
            ></model-viewer>
          </div>
        </div>
      </section>

      {/* --- Latest Updates (Auto Linked to Courses) --- */}
      <section className="l-section updates-section l-fade-in" aria-label="Latest ICT Courses and Products">
        <h2 className="l-section-title">Latest <span className="l-highlight">Updates</span></h2>
        <div className="updates-grid">
          {latestUpdates.map((item) => (

            <Link to={item.link} key={item.id} className="update-card has-img" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="card-img-holder">
                <img src={item.img} alt={item.title} />
              </div>
              <div className="card-content">
                <span className={`tag ${item.tag.toLowerCase()}`}>{item.tag}</span>
                <h3>{item.title}</h3>
                <p className="date">{item.date}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* --- Offers --- */}
      <section className="l-section offers-section l-fade-in" id="store" aria-label="Store Offers and Discounts">
        <h2 className="l-section-title">Store <span className="l-highlight">Offers</span></h2>
        <div className="offers-grid">
          <div className="offer-card">
            <h3>Summer Sale - 50% OFF</h3>
            <p>On all ICT Courses. Use Code: <b>SUMMER50</b></p>
          </div>
          <div className="offer-card">
            <h3>Electronics Bundle</h3>
            <p>Buy Kit + Guide Book. Use Code: <b>KIT2025</b></p>
          </div>
        </div>
      </section>

      {/* --- Contact --- */}

      <Contact />

      <LandingFooter />
    </div>
  );
};

export default LandingPage;